import { MessagePrimitive, useAuiState } from '@assistant-ui/react';
import React from 'react';

import { CollapsibleToolRun } from './CollapsibleToolRun.tsx';
import { AssistantBubble } from '#plugin-chat/components/bubble/AssistantBubble.tsx';
import { ProgressBubble } from '#plugin-chat/components/thread/ProgressBubble.tsx';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { isCollapsibleTool } from '#plugin-chat/lib/tool/progress-registry.ts';

/**
 * Segment of message parts to render as a single unit.
 *
 * - `passthrough`: a non-collapsible part (text, reasoning, non-collapsible
 *   tool, etc.) rendered via `MessagePrimitive.PartByIndex` with the same
 *   component overrides the current thread uses — byte-identical to today.
 * - `collapsible-run`: one or more consecutive collapsible tool parts rendered
 *   together inside a `CollapsibleToolRun`.
 */
type PartSegment = { kind: 'passthrough'; index: number } | { kind: 'collapsible-run'; indices: number[] };

/**
 * Walk message parts left-to-right and group consecutive collapsible tool-call
 * parts into runs. Every other part becomes its own `passthrough` segment.
 *
 * Collapsibility is determined at part-scan time by `isCollapsibleTool(toolName)`,
 * which reads the module-level registry populated by `createTool`. Tools without
 * a `progress` declaration are never grouped — their rendering path is unchanged.
 */
function useGroupedParts(): PartSegment[] {
  const parts = useAuiState((state) => state.message.parts);
  const isCollapsible = (part: { type: string; toolName?: string }): boolean =>
    part.type === 'tool-call' && !!part.toolName && isCollapsibleTool(part.toolName);

  return parts.reduce<PartSegment[]>((segments, part, index) => {
    if (!isCollapsible(part)) {
      segments.push({ kind: 'passthrough', index });
      return segments;
    }
    const last = segments.at(-1);
    if (last?.kind === 'collapsible-run') {
      last.indices.push(index);
    } else {
      segments.push({ kind: 'collapsible-run', indices: [index] });
    }
    return segments;
  }, []);
}

/**
 * Top-level message-parts renderer for assistant messages.
 *
 * Replaces the previous single `<MessagePrimitive.Parts>` call. Splits parts
 * into segments — consecutive collapsible tool calls become one
 * `CollapsibleToolRun`; every other part passes through unchanged via
 * `MessagePrimitive.PartByIndex` with the same component overrides the thread
 * has always used (`Text: AssistantBubble`, `tools.Fallback: ToolDisplay`).
 */
export function CollapsibleMessageParts() {
  const parts = useAuiState((state) => state.message.parts);
  const segments = useGroupedParts();
  const isRunning = useAuiState((state) => state.message.status?.type === 'running');

  if (!segments.length && isRunning) {
    return <ProgressBubble />;
  }

  return segments.map((segment, segmentIndex) => {
    if (segment.kind === 'collapsible-run') {
      const lastIndex = segment.indices.at(-1);
      const lastPart = lastIndex === undefined ? undefined : parts.at(lastIndex);
      const toolPart = lastPart?.type === 'tool-call' ? lastPart : undefined;
      const isLastPart = segment.indices.some((i) => i >= parts.length - 1);
      return (
        <React.Fragment
          // biome-ignore lint/suspicious/noArrayIndexKey: Segment order is stable within a message render.
          key={`run-${segmentIndex}`}
        >
          <CollapsibleToolRun indices={segment.indices} />
          {isRunning && isLastPart && <ProgressBubble toolPart={toolPart} />}
        </React.Fragment>
      );
    }

    const lastPart = parts.at(-1);
    const toolPart = lastPart?.type === 'tool-call' ? lastPart : undefined;
    return (
      <React.Fragment key={`part-${segment.index}`}>
        <MessagePrimitive.PartByIndex
          components={{
            Text: AssistantBubble,
            tools: { Fallback: ToolDisplay },
          }}
          index={segment.index}
        />
        {isRunning && segmentIndex === segments.length - 1 && (
          <ProgressBubble fallbackComponent={null} toolPart={toolPart} />
        )}
      </React.Fragment>
    );
  });
}
